import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

/** The banner's daily window opens at 2pm Eastern. A fixed IANA zone, not a "-05:00" offset, so
 *  the boundary stays at local 2pm across the DST switch rather than drifting to 1pm or 3pm. */
export const WINDOW_TIMEZONE = "America/New_York";
export const WINDOW_OPEN_HOUR = 14;

/** 2pm Eastern on the Eastern calendar day containing `now`. */
export function dailyWindowStart(now: Date): Date {
  // Wall-clock Eastern time expressed in the local fields of a Date, which is what
  // `zonedTimeToUtc` expects back.
  const easternWallClock = utcToZonedTime(now, WINDOW_TIMEZONE);
  easternWallClock.setHours(WINDOW_OPEN_HOUR, 0, 0, 0);

  return zonedTimeToUtc(easternWallClock, WINDOW_TIMEZONE);
}

/**
 * Whether this page load is the one that shows the banner.
 *
 * The banner is a once-a-day notice pinned to a time of day: it is invisible before 2pm Eastern,
 * appears on the first page load at or after it, and then stays gone until the next day's 2pm.
 * Pinning it to a clock time rather than a rolling 24h window means it surfaces at one predictable
 * moment for the whole practice instead of at whatever minute each user first opened the app.
 *
 * The accepted cost is that a connection which breaks in the morning is not announced until 2pm,
 * and that a user whose day ends before 2pm never sees it at all — for them the existing email to
 * the business owner remains the only signal.
 */
export function shouldShowNow(
  shownAt: string | undefined,
  now: Date = new Date()
): boolean {
  const windowStart = dailyWindowStart(now);

  if (now.getTime() < windowStart.getTime()) return false;
  if (!shownAt) return true;

  const shownAtDate = new Date(shownAt);
  // A corrupt or hand-edited timestamp must not hide an actionable banner forever.
  if (Number.isNaN(shownAtDate.getTime())) return true;

  return shownAtDate.getTime() < windowStart.getTime();
}
